import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import PropTypes from "prop-types";

import { copyTextToClipboard } from "utils";

import ServerSearch from "views/Components/ServerSearch";
import ServerTable from "../ServerTable";
import { entityFields } from "../../Pages/SimpleSearch/defs";
import { navToInvestigation } from "../../Pages/SimpleSearch/helpers";
import { useQueryControls } from "views/Components/ServerQueryControls";

import { SUMMARY_TO_BQ_FIELD_MAP } from "lib/models/BQSummary";

// Calculate column widths for the evidence table based on average length
// of text within each column.
const calculateColumnWidths = (rowWidths) => {
  const colWidths = {};

  Object.entries(rowWidths).forEach(([field, cellWidths]) => {
    const totalWidth = cellWidths.reduce((acc, cur) => acc + cur, 0);
    const avgWidth = totalWidth / cellWidths.length;

    colWidths[field] = avgWidth;

    if (field.length > avgWidth) {
      // If the header is longer, use that to compute the column width
      colWidths[field] = field.length * 4;
    } else {
      // Scale the text width to something close-ish to column width
      colWidths[field] *= 6;
    }

    // Don't set width greater than 350
    if (colWidths[field] > 350) {
      colWidths[field] = 350;
    }
  });
  return colWidths;
};

const handleContextMenuCopyElementText = (clickedRow) => {
  const anchorEl = clickedRow?.event?.target.closest(".MuiDataGrid-cell");
  copyTextToClipboard(anchorEl.innerText);
};

const formatColumns = (rowWidths) => {
  const colWidths = calculateColumnWidths(rowWidths);
  const uniqueKeys = Object.keys(colWidths).sort();
  const columns = uniqueKeys
    .filter((uniqueKey) => uniqueKey !== "timeMatched")
    .map((uniqueKey) => {
      return {
        field: String(uniqueKey),
        maxWidth: 400,
        minWidth: colWidths[uniqueKey],
        sortable: false,
      };
    });
  return [
    {
      title: "Time Matched",
      field: "timeMatched",
      minWidth: 140,
      sortable: false,
    },
    ...columns,
  ];
};

const isObject = (value) => {
  return typeof value === "object";
};

const formatValue = (value) => {
  if (isObject(value)) {
    if (Array.isArray(value)) return value.join(", ");
    return JSON.stringify(value, null, 2);
  }
  return value;
};

const formatIndividualRow = (row, index) => {
  // id is for search capability
  let rowToReturn = { id: `${index}`, timeMatched: row.created };
  Object.entries(row.evidence).forEach(
    (el) => (rowToReturn[el[0]] = formatValue(el[1]))
  );
  return rowToReturn;
};

const formatData = (initialData) =>
  initialData.map((row, i) => formatIndividualRow(row, i));

const Evidence = ({ initialData, license, matchId, orgId, superadmin }) => {
  const [columns, setColumns] = useState([]);
  const [evidenceTimeFrame, setEvidenceTimeFrame] = useState({});
  const [queryControls, queryControlInterface] = useQueryControls("evidence");

  useEffect(() => {
    // If no data provided, no need to update
    let minTimeMatched = "ZZ";
    let maxTimeMatched = "";
    if (initialData.length) {
      // Collect some info from the initial data
      const formattedData = formatData(initialData);
      const rowWidths = {};
      formattedData.forEach((row) => {
        Object.keys(row).forEach((k) => {
          rowWidths[k] = rowWidths[k] || [];
          rowWidths[k].push(row[k].length);
        });
        if (row.timeMatched > maxTimeMatched) {
          maxTimeMatched = row.timeMatched;
        }
        if (row.timeMatched < minTimeMatched) {
          minTimeMatched = row.timeMatched;
        }
      });

      // Format data to how our table likes it.
      // happy table = happy life
      const formattedColumns = formatColumns(rowWidths);
      setColumns(formattedColumns);
    }
    if (maxTimeMatched !== "") {
      const timeframeStart =
        moment(minTimeMatched).add(-5, "days").utc().unix() * 1000;

      const currentTime = moment().utc();
      const proposedEnd = moment(maxTimeMatched).add(5, "days").utc();
      // If adding +5 days to the evidence time gives us a future value, then end the search at the current time
      const timeframeEnd = proposedEnd.isAfter(currentTime)
        ? currentTime.unix() * 1000
        : proposedEnd.unix() * 1000;

      setEvidenceTimeFrame({ start: timeframeStart, end: timeframeEnd });
    }
  }, [initialData]);

  const getContextMenus = () => {
    const menus = [
      {
        onClick: handleContextMenuCopyElementText,
        text: "Copy to Clipboard",
      },
    ];
    if (license.isAllowedPage("investigate/search") || superadmin) {
      const allReduced = [];
      Object.keys(entityFields).map((k) => {
        if (SUMMARY_TO_BQ_FIELD_MAP[k]) {
          allReduced.push(
            Object.values(SUMMARY_TO_BQ_FIELD_MAP[k]).reduce(
              (a, v) => (a = a.union(v)),
              new Set([])
            )
          );
        }
      });

      menus.push({
        onClick: ({ details, event, params }) => {
          // TODO Using values greater than ~50 characters as the search
          // term seems to cause errors. Truncate at 50 for now.
          const cellValue = event.target.textContent.slice(0, 50);
          navToInvestigation(orgId, {
            search: cellValue,
            ...evidenceTimeFrame,
          });
        },
        limitToFields: [
          ...allReduced.reduce((a, v) => (a = a.union(v)), new Set([])),
        ],
        text: "Investigate in new window",
      });
    }
    return menus;
  };

  // Avoid unnecessary rerenders because they cause duplicate requests
  const contextMenus = useMemo(() => {
    return getContextMenus();
  }, [evidenceTimeFrame, license, orgId, superadmin]);

  const params = useMemo(() => {
    const params = [{ field: "matchId", value: matchId }];
    return params.concat(Object.values(queryControls));
  }, [matchId, queryControls, orgId]);

  return (
    <>
      <ServerSearch
        controlInterface={queryControlInterface}
        controls={queryControls}
      />
      <ServerTable
        columns={columns}
        contextMenuItems={contextMenus}
        defaultSort={[{ field: "created", sort: "desc" }]}
        endpoint="/evidence"
        formatData={formatData}
        orgId={orgId}
        pageSizeOptions={[10, 25, 50, 100, 250]}
        params={params}
        sortable={true}
        uniqueName="FindingDetailEvidence"
      />
    </>
  );
};

Evidence.propTypes = {
  initialData: PropTypes.arrayOf(PropTypes.object).isRequired,
  license: PropTypes.object.isRequired,
  matchId: PropTypes.string.isRequired,
  orgId: PropTypes.string.isRequired,
  superadmin: PropTypes.bool.isRequired,
};

Evidence.defaultProps = {
  superadmin: false,
};

export default Evidence;
